import { reactive } from 'vue';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

export type ToastItem = {
  id: string;
  open: boolean;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
};

export const TOAST_LIMIT = 5;
export const TOAST_REMOVE_DELAY = 320;

export const toastState = reactive({
  toasts: [] as ToastItem[],
});

let toastCount = 0;
const removeTimers = new Map<string, ReturnType<typeof setTimeout>>();

function genId() {
  toastCount = (toastCount + 1) % Number.MAX_SAFE_INTEGER;
  return String(toastCount);
}

function scheduleRemove(id: string) {
  if (removeTimers.has(id)) return;
  const timer = setTimeout(() => {
    removeTimers.delete(id);
    toastState.toasts = toastState.toasts.filter((item) => item.id !== id);
  }, TOAST_REMOVE_DELAY);
  removeTimers.set(id, timer);
}

export function pushToast(input: {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}) {
  const variant = input.variant ?? 'default';
  const existing = toastState.toasts.find((toast) =>
    toast.open &&
    toast.title === input.title &&
    toast.description === input.description &&
    toast.variant === variant
  );
  if (existing) {
    return existing.id;
  }

  const id = genId();
  const item: ToastItem = {
    id,
    open: true,
    title: input.title,
    description: input.description,
    variant,
    duration: input.duration,
  };

  toastState.toasts.unshift(item);
  if (toastState.toasts.length > TOAST_LIMIT) {
    const removed = toastState.toasts.splice(TOAST_LIMIT);
    for (const toast of removed) {
      const timer = removeTimers.get(toast.id);
      if (timer) {
        clearTimeout(timer);
        removeTimers.delete(toast.id);
      }
    }
  }

  return id;
}

export function dismissToast(id: string) {
  const toast = toastState.toasts.find((item) => item.id === id);
  if (!toast || !toast.open) return;
  toast.open = false;
  scheduleRemove(id);
}

export function handleToastOpenChange(id: string, open: boolean) {
  const toast = toastState.toasts.find((item) => item.id === id);
  if (!toast) return;
  toast.open = open;
  if (!open) {
    scheduleRemove(id);
  }
}

export function resetToastStateForTests() {
  toastState.toasts = [];
  for (const timer of removeTimers.values()) {
    clearTimeout(timer);
  }
  removeTimers.clear();
  toastCount = 0;
}
